// Spec §4.3: Extract debounce click logic from CustomButton component
import dayjs from 'dayjs';
import React, { useRef } from 'react';
import type { ICustomButtonProps } from './types';

/**
 * Stand-in click handler used when the custom button receives no onClick prop. Performs no work.
 *
 * @returns Nothing.
 */
const NOOP = () => {};

/**
 * Supplies the custom button with a click handler that guards against double submits. Defaults the onClick prop to a no-op and, unless debouncing is disabled, drops clicks arriving within 300ms of the previous one recorded in a ref.
 *
 * @param props - Button props carrying onClick and isDebounceClick.
 * @returns The debounced click handler.
 */
export const useCustomButton = (props: ICustomButtonProps) => {
  const { isDebounceClick = true, onClick: onClickProp = NOOP } = props;
  const debounceClickBottomButton = useRef(0);

  /**
   * Debounced click handler for the custom button. Returns early when debouncing is enabled and less than 300ms has passed since the recorded previous click, otherwise stores the current timestamp and invokes the wrapped onClick prop.
   *
   * @param e - The button click event.
   * @returns Nothing.
   */
  const onClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    if (isDebounceClick && dayjs().valueOf() - debounceClickBottomButton.current < 300) {
      return;
    }

    debounceClickBottomButton.current = dayjs().valueOf();
    onClickProp(e);
  };

  return { onClick };
};
